import React, { useEffect, useRef, useState } from 'react';
import PerfisAdicionais from '../perfis-adicionais/PerfisAdicionais';
import { registerUser } from '../../../api/authApi';
import { notify } from '../../../shared/feedback/notify';
import { useIsTi } from '../../../hooks/useUserType';

interface RegisterUserModalProps {
  isOpen: boolean;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  onSuccess?: () => void;
}

const RegisterUserModal: React.FC<RegisterUserModalProps> = ({ isOpen, onCancel, sidebarRef, onSuccess }) => {
  const [username, setUsernameState] = useState<string>('');
  const [email, setEmailState] = useState<string>('');
  const [fullName, setFullNameState] = useState<string>('');
  const [password, setPasswordState] = useState<string>('');
  const [confirmPassword, setConfirmPasswordState] = useState<string>('');
  const [userType, setUserTypeState] = useState<string>('COLABORADOR');
  // at-81: perfis ALÉM do principal. O acesso do usuário é a união dos dois.
  const [perfisAdicionais, setPerfisAdicionais] = useState<string[]>([]);
  const [codigoVoluntario, setCodigoVoluntario] = useState<string>('');
  const [codigoRegional, setCodigoRegional] = useState<string>('');
  const modalRef = useRef<HTMLDivElement>(null);
  // TI gerencia usuários mas não pode criar administradores (at-25).
  const isTi = useIsTi();

  const resetForm = () => {
    setUsernameState('');
    setEmailState('');
    setFullNameState('');
    setPasswordState('');
    setConfirmPasswordState('');
    setUserTypeState('COLABORADOR');
    setCodigoVoluntario('');
    setCodigoRegional('');
  };

  const normalizeNullableNumber = (value: string, fieldLabel: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) throw new Error(`${fieldLabel} deve ser numérico.`);
    return parsed;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      notify.error('As senhas não coincidem.');
      return;
    }

    try {
      await registerUser({
        username,
        email,
        password,
        confirmPassword,
        userType,
        papeis: [userType, ...perfisAdicionais],
        fullName,
        isAdmin: [userType, ...perfisAdicionais].includes('ADMIN'),
        codigoVoluntario: normalizeNullableNumber(codigoVoluntario, 'Código do voluntário'),
        codigoRegional: normalizeNullableNumber(codigoRegional, 'Código da regional'),
      });

      notify.success('Usuario criado com sucesso!');
      resetForm();
      onCancel();
      onSuccess?.();
    } catch (error) {
      console.error('Erro ao fazer registro', error);
      notify.errorFrom(error, 'Erro ao registrar usuário');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-car-modal-overlay update-user-modal-specific">
      <div className="edit-car-modal-content" ref={modalRef}>
        <div className="lightbox-update-user">
          <form className="LightboxLogin" onSubmit={handleRegister}>
            <div className="EditarCarro">Criar Novo Usuário</div>

            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Nome completo</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      name="fullName"
                      value={fullName}
                      onChange={(e) => setFullNameState(e.target.value)}
                    />
                  </div>
                </div>

                <div className="Campo">
                  <div className="CampoTitulo">Usuário</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      name="username"
                      value={username}
                      onChange={(e) => setUsernameState(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Setor</div>
                  <div className="CampoInput">
                    <select
                      className="Input"
                      name="userType"
                      value={userType}
                      onChange={(e) => setUserTypeState(e.target.value)}
                    >
                      {/* Ordem alfabética pelo RÓTULO (o que se lê), não pelo enum: a lista
                          passou de 8 para 17 papéis e procurar num select fora de ordem é o que
                          faz alguém escolher o vizinho errado (pedido do Victor, 25/09/2026). */}
                      {!isTi && <option value="ADMIN">ADMIN</option>}
                      <option value="ADMINISTRACAO">Administração</option>
                      <option value="CHEFE_DE_OFICINA">Chefe de Oficina</option>
                      <option value="COLABORADOR">Colaborador</option>
                      <option value="COMISSIONAMENTO">Comissionamento</option>
                      <option value="COMPRA">Compra</option>
                      <option value="CONSULTOR">Consultor</option>
                      <option value="CONTAC">Contac</option>
                      <option value="ESTOQUE">Estoque</option>
                      <option value="EVENTOS">Eventos</option>
                      <option value="GESTAO_COMPRAS">Gestão de Compras</option>
                      <option value="INSPETOR">Inspetor</option>
                      <option value="TELAO">Telão</option>
                      <option value="ORCAMENTO">Orçamento</option>
                      <option value="RASTREADOR">Rastreador</option>
                      <option value="RECEPCAO">Recepção</option>
                      <option value="REGIONAL">Regional</option>
                      <option value="RELACIONAMENTO">Relacionamento</option>
                      <option value="TI">TI</option>
                    </select>
                  </div>
                </div>

                <div className="Campo">
                  <PerfisAdicionais
                    papelPrincipal={userType}
                    selecionados={perfisAdicionais}
                    onChange={setPerfisAdicionais}
                    ocultarAdmin={isTi}
                  />
                </div>

                <div className="Campo">
                  <div className="CampoTitulo">Email</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmailState(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Senha</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPasswordState(e.target.value)}
                    />
                  </div>
                </div>

                <div className="Campo">
                  <div className="CampoTitulo">Confirmar Senha</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="password"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPasswordState(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Código do Voluntário</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      inputMode="numeric"
                      name="codigoVoluntario"
                      value={codigoVoluntario}
                      onChange={(e) => setCodigoVoluntario(e.target.value)}
                    />
                  </div>
                </div>

                <div className="Campo">
                  <div className="CampoTitulo">Código da Regional</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      inputMode="numeric"
                      name="codigoRegional"
                      value={codigoRegional}
                      onChange={(e) => setCodigoRegional(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="Frame1000003998">
              <div className="CtaBigEditar cancel" onClick={onCancel}>
                <button className="CustomFileCancel" type="button">Cancelar</button>
              </div>
              <button className="CtaBigEditar save" type="submit">Criar Usuário</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterUserModal;
